function trimText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isSpanishLanguage(language = "en") {
  return String(language ?? "").toLowerCase().startsWith("es");
}

function getLocalizedText(value, language = "en") {
  if (typeof value === "string") {
    return value.trim();
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  const primaryKey = isSpanishLanguage(language) ? "es" : "en";
  const secondaryKey = primaryKey === "es" ? "en" : "es";
  const primaryValue = trimText(value?.[primaryKey]);
  if (primaryValue) {
    return primaryValue;
  }

  const secondaryValue = trimText(value?.[secondaryKey]);
  if (secondaryValue) {
    return secondaryValue;
  }

  return Object.values(value).map(trimText).find(Boolean) ?? "";
}

function getDuplicatePrefix(language = "en") {
  return isSpanishLanguage(language) ? "Copia de" : "Copy of";
}

function getEntityLabels(entityType, language = "en") {
  const labelsByType = {
    plan: isSpanishLanguage(language)
      ? {
          base: "Plan nutricional",
          untitled: "Plan nutricional sin nombre",
        }
      : {
          base: "Nutrition plan",
          untitled: "Untitled nutrition plan",
        },
    recipe: isSpanishLanguage(language)
      ? {
          base: "Receta",
          untitled: "Receta sin nombre",
        }
      : {
          base: "Recipe",
          untitled: "Untitled recipe",
        },
    equivalent: isSpanishLanguage(language)
      ? {
          base: "Equivalencia",
          untitled: "Equivalencia sin nombre",
        }
      : {
          base: "Equivalent",
          untitled: "Untitled equivalent",
        },
  };

  return labelsByType[entityType] ?? labelsByType.plan;
}

export function resolveNutritionEntityName(...sources) {
  for (const source of sources) {
    const value = typeof source === "string" ? source : source?.name;
    const resolvedName = getLocalizedText(value, "en") || getLocalizedText(value, "es");
    if (resolvedName) {
      return resolvedName;
    }
  }

  return "";
}

export function resolveNutritionEntityDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    const nextDate = new Date(value.getTime());
    return Number.isFinite(nextDate.getTime()) ? nextDate : null;
  }

  if (typeof value?.toDate === "function") {
    const resolvedDate = value.toDate();
    if (resolvedDate instanceof Date && Number.isFinite(resolvedDate.getTime())) {
      return resolvedDate;
    }
  }

  const parsedDate = new Date(value);
  return Number.isFinite(parsedDate.getTime()) ? parsedDate : null;
}

export function formatNutritionEntityShortDate(value, language = "en") {
  const resolvedDate = resolveNutritionEntityDateValue(value);
  if (!resolvedDate) {
    return "";
  }

  return resolvedDate.toLocaleDateString(
    isSpanishLanguage(language) ? "es-ES" : "en-US",
    {
      day: "numeric",
      month: "short",
    },
  );
}

export function getNutritionEntityDisplayName(entityType, entityLike, language = "en") {
  const resolvedName = getLocalizedText(
    typeof entityLike === "string" ? entityLike : entityLike?.name,
    language,
  );
  if (resolvedName) {
    return resolvedName;
  }

  const labels = getEntityLabels(entityType, language);
  const formattedDate = formatNutritionEntityShortDate(entityLike?.createdAt, language);
  if (formattedDate) {
    return `${labels.base} - ${formattedDate}`;
  }

  return labels.untitled;
}

export function getNutritionEntityDisplayNameFromSources(
  entityType,
  sources = [],
  language = "en",
) {
  const sourceList = Array.isArray(sources) ? sources : [sources];

  for (const source of sourceList) {
    const resolvedName = getLocalizedText(
      typeof source === "string" ? source : source?.name,
      language,
    );
    if (resolvedName) {
      return resolvedName;
    }
  }

  for (const source of sourceList) {
    const formattedDate = formatNutritionEntityShortDate(source?.createdAt, language);
    if (formattedDate) {
      return getNutritionEntityDisplayName(entityType, source, language);
    }
  }

  return getNutritionEntityUntitledLabel(entityType, language);
}

export function getNutritionEntityUntitledLabel(entityType, language = "en") {
  return getEntityLabels(entityType, language).untitled;
}

export function buildNutritionEntityDuplicateName(
  entityType,
  entityLike,
  language = "en",
) {
  return `${getDuplicatePrefix(language)} ${getNutritionEntityDisplayNameFromSources(
    entityType,
    entityLike,
    language,
  )}`;
}
